import { readFileSync, existsSync } from "node:fs";
import { join } from "node:path";
import { homedir } from "node:os";

// ── Types ────────────────────────────────────────────────────

export interface DailyTokenBucket {
  date: string; // "2026-01-20"
  tokensByModel: Record<string, number>; // model → total tokens (combined in/out)
}

export interface ModelUsage {
  inputTokens: number;
  outputTokens: number;
  cacheReadInputTokens: number;
  cacheCreationInputTokens: number;
}

export interface StatsCache {
  version: number;
  lastComputedDate: string;
  dailyModelTokens: DailyTokenBucket[];
  modelUsage: Record<string, ModelUsage>; // lifetime aggregates by model
}

// ── Reader ───────────────────────────────────────────────────

/**
 * Read and parse ~/.claude/stats-cache.json.
 * Returns null if the file doesn't exist or can't be parsed.
 */
export function readStatsCache(
  customPath?: string,
): StatsCache | null {
  const filePath =
    customPath ?? join(homedir(), ".claude", "stats-cache.json");

  if (!existsSync(filePath)) return null;

  try {
    const raw = JSON.parse(readFileSync(filePath, "utf-8"));

    // Validate minimum expected shape
    if (
      !raw.dailyModelTokens ||
      !Array.isArray(raw.dailyModelTokens) ||
      !raw.modelUsage ||
      typeof raw.modelUsage !== "object"
    ) {
      return null;
    }

    return {
      version: raw.version ?? 1,
      lastComputedDate: raw.lastComputedDate ?? "",
      dailyModelTokens: raw.dailyModelTokens as DailyTokenBucket[],
      modelUsage: raw.modelUsage as Record<string, ModelUsage>,
    };
  } catch {
    return null;
  }
}

/**
 * Get daily token totals for a specific date.
 * Returns a map of model → total tokens for that day.
 */
export function getDailyTokens(
  cache: StatsCache,
  date: string,
): Record<string, number> {
  const bucket = cache.dailyModelTokens.find((d) => d.date === date);
  return bucket?.tokensByModel ?? {};
}

/**
 * Get the input/output ratio for a model from lifetime aggregates.
 * Used to split daily totals (which are combined) into input/output.
 * Returns { inputRatio, outputRatio } where both sum to 1.
 */
export function getModelTokenRatios(
  cache: StatsCache,
  model: string,
): { inputRatio: number; outputRatio: number } {
  // Try exact match, then prefix match (e.g., "claude-opus-4-6" matches "claude-opus-4-6-...")
  const usage =
    cache.modelUsage[model] ??
    Object.entries(cache.modelUsage).find(([k]) =>
      k.startsWith(model) || model.startsWith(k),
    )?.[1];

  if (!usage) {
    // Default 30/70 split (typical input/output ratio)
    return { inputRatio: 0.3, outputRatio: 0.7 };
  }

  const total = usage.inputTokens + usage.outputTokens;
  if (total === 0) {
    return { inputRatio: 0.3, outputRatio: 0.7 };
  }

  return {
    inputRatio: usage.inputTokens / total,
    outputRatio: usage.outputTokens / total,
  };
}
